import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import { buttonBaseClasses, buttonClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { ProjectStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { cloneElement, useCallback, useMemo, useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Project } from '~hooks/useProjects/models';
import { useStores } from '~store';

type MenuAction = {
  disabled?: boolean;
  icon: React.ReactElement;
  key: string;
  label: string;
  onClick: () => void;
};

interface ProjectActionsMenuProps {
  isGeneratingPdf?: boolean;
  onCopyAndEditProjectClick?: (project: Project) => void;
  onDeleteProjectClick?: (project: Project) => void;
  onEmailQuotePdfClick?: (project: Project) => void;
  onToggleProjectActiveStateClick?: (project: Project) => Promise<void>;
  onViewQuotePdfClick?: (project: Project) => void;
  project: Project;
}

export function ProjectActionsMenu({
  isGeneratingPdf,
  onCopyAndEditProjectClick,
  onDeleteProjectClick,
  onEmailQuotePdfClick,
  onToggleProjectActiveStateClick,
  onViewQuotePdfClick,
  project,
}: ProjectActionsMenuProps) {
  const theme = useTheme();
  const { userStore } = useStores();

  const permissions = userStore.getPermissions();
  const canCopyAndEditProject = Boolean(
    permissions.canCreateProject && onCopyAndEditProjectClick,
  );
  const canDeleteProject = Boolean(permissions.canDeleteProject && onDeleteProjectClick);
  const canToggleProjectActiveState = Boolean(
    permissions.canEditProject && onToggleProjectActiveStateClick,
  );
  // The quote PDF is a rates document: without view_rate the server nils every
  // rate value, so the PDF would render with blank rate sections — hide the
  // action entirely for those users.
  const canViewQuotePdf = Boolean(permissions.canViewRate && onViewQuotePdfClick);
  const canEmailQuotePdf = Boolean(permissions.canViewRate && onEmailQuotePdfClick);

  const menuHandler = useRef<MenuHandler>(null);

  const [isTogglingProjectActiveState, setIsTogglingProjectActiveState] = useState(false);

  const canActivate = project.stateEvents.includes(ProjectStateEvent.ACTIVATE);
  const canDeactivate = project.stateEvents.includes(ProjectStateEvent.DEACTIVATE);

  const closeMenu = useCallback(() => {
    menuHandler.current?.onClose?.();
  }, []);

  const handleCopyAndEditProjectClick = useCallback(() => {
    onCopyAndEditProjectClick?.(project);
    closeMenu();
  }, [closeMenu, onCopyAndEditProjectClick, project]);

  const handleDeleteProjectClick = useCallback(() => {
    onDeleteProjectClick?.(project);
    closeMenu();
  }, [closeMenu, onDeleteProjectClick, project]);

  const handleViewQuotePdfClick = useCallback(() => {
    onViewQuotePdfClick?.(project);
    closeMenu();
  }, [closeMenu, onViewQuotePdfClick, project]);

  const handleEmailQuotePdfClick = useCallback(() => {
    onEmailQuotePdfClick?.(project);
    closeMenu();
  }, [closeMenu, onEmailQuotePdfClick, project]);

  const handleToggleProjectActiveStateClick = useCallback(async () => {
    try {
      setIsTogglingProjectActiveState(true);
      await onToggleProjectActiveStateClick?.(project);
    } finally {
      setIsTogglingProjectActiveState(false);
      closeMenu();
    }
  }, [closeMenu, onToggleProjectActiveStateClick, project]);

  const actions = useMemo(() => {
    const items: Array<MenuAction> = [];

    if (canViewQuotePdf) {
      items.push({
        disabled: isGeneratingPdf,
        icon: <PictureAsPdfOutlined />,
        key: 'view_quote_pdf',
        label: t('project.view_pdf'),
        onClick: handleViewQuotePdfClick,
      });
    }

    if (canEmailQuotePdf) {
      items.push({
        icon: <EmailOutlined />,
        key: 'email_quote_pdf',
        label: t('project.email_pdf'),
        onClick: handleEmailQuotePdfClick,
      });
    }

    if (canCopyAndEditProject) {
      items.push({
        icon: <FileCopyOutlined />,
        key: 'copy_and_edit_project',
        label: t('project.copy_and_edit'),
        onClick: handleCopyAndEditProjectClick,
      });
    }

    if (canDeleteProject) {
      items.push({
        icon: <DeleteOutlined color="error" />,
        key: 'delete_project',
        label: t('actions.delete'),
        onClick: handleDeleteProjectClick,
      });
    }

    if (canToggleProjectActiveState && canDeactivate) {
      items.push({
        disabled: isTogglingProjectActiveState,
        icon: <ToggleOffOutlined />,
        key: 'deactivate_project',
        label: t('actions.deactivate'),
        onClick: handleToggleProjectActiveStateClick,
      });
    }

    if (canToggleProjectActiveState && canActivate) {
      items.push({
        disabled: isTogglingProjectActiveState,
        icon: <ToggleOnOutlined color="primary" />,
        key: 'activate_project',
        label: t('actions.activate'),
        onClick: handleToggleProjectActiveStateClick,
      });
    }

    return items;
  }, [
    canActivate,
    canCopyAndEditProject,
    canDeactivate,
    canDeleteProject,
    canEmailQuotePdf,
    canToggleProjectActiveState,
    canViewQuotePdf,
    handleCopyAndEditProjectClick,
    handleDeleteProjectClick,
    handleEmailQuotePdfClick,
    handleToggleProjectActiveStateClick,
    handleViewQuotePdfClick,
    isGeneratingPdf,
    isTogglingProjectActiveState,
  ]);

  if (!actions.length) {
    return null;
  }

  return (
    <>
      <Menu
        ref={menuHandler}
        menuTrigger={
          <SmallButton
            color="brandV2TreadGray3"
            variant="text"
            // Stable e2e hook: this icon-only trigger has no accessible name and
            // MUI's dev-only `data-testid="MoreHorizIcon"` is stripped in prod
            // builds, so it can't anchor a staging selector.
            data-test-id="project-actions-menu-trigger"
            startIcon={<MoreHoriz />}
            sx={{
              [`&.${buttonBaseClasses.root}`]: {
                border: 'solid 1px transparent',
                borderRadius: '50%',
                height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                p: 0,
                width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                [`.${buttonClasses.startIcon}`]: { mx: 0 },
              },
            }}
          />
        }
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            data-test-id={`project-action-${action.key}`}
            disabled={action.disabled}
            onClick={(event) => {
              event.stopPropagation();
              if (!action.disabled) {
                action.onClick();
              }
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                gap: 1,
              }}
            >
              {cloneElement(action.icon, {
                sx: { fontSize: '16px' },
              })}

              <Typography
                color={theme.brandV2.colors.treadBlack}
                sx={{
                  fontSize: '12px',
                }}
              >
                {action.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
